import React from 'react';
import { makeStyles, rgba } from 'design-system/styles';
import {
  INamedEntityRecognitionAnnotation,
  INamedEntityRecognitionRelationship,
} from './namedEntityRecognitionAnnotations';
import NERLinkPopover from './NERLinkPopover';
import { IColorMap } from './namedEntityRecognitionLabels';

const PADDING_PER_DEPTH = 16;
const BASE_PADDING = 7;
const CURVE_RADIUS = 10;
const BEZIER_CONTROL_DISTANCE = 0.55 * CURVE_RADIUS; // https://stackoverflow.com/questions/1734745/how-to-create-circle-with-b%C3%A9zier-curves

const ARROW_HEIGHT = 6;
const ARROW_WIDTH = 6;
const ARROW_PADDING = 5;

const LINK_COLOR = '#666666'; // roughly rgba('#000', 0.4), useful in non-alpha form for stacking

interface IClientBox {
  top: number;
  left: number;
  height: number;
  width: number;
}

export enum NERLinkDirection {
  TopToBottom = 'topToBottom',
  BottomToTop = 'bottomToTop',
  Undirected = 'undirected',
}

interface INERLinkProps {
  isPreview?: boolean;
  displayName?: string;
  direction?: NERLinkDirection;
  onPreviewClose?: () => void;
  currentRelationshipId?: string;
  validRelationships: INamedEntityRecognitionRelationship[];
  onSetRelationship: (
    relationship: INamedEntityRecognitionRelationship,
    currentRelationshipId?: string,
  ) => void;
  toggleRelationshipVisibility: (id: string) => void;
  isMultiLine: boolean;
  topAnnotation: INamedEntityRecognitionAnnotation;
  topAnnotationClientBox: IClientBox;
  topAnnotationAnchorOffset: number;
  topAnnotationLinkDepth: number;
  bottomAnnotation: INamedEntityRecognitionAnnotation;
  bottomAnnotationClientBox: IClientBox;
  bottomAnnotationAnchorOffset: number;
  bottomAnnotationLinkDepth: number;
  labelColormap: IColorMap;
  containerWidth: number;
}

const NERLink: React.ForwardRefRenderFunction<HTMLElement, INERLinkProps> = (
  {
    isPreview,
    displayName,
    direction = NERLinkDirection.Undirected,
    onPreviewClose,
    currentRelationshipId,
    validRelationships,
    onSetRelationship,
    toggleRelationshipVisibility,
    isMultiLine,
    topAnnotationClientBox,
    topAnnotationAnchorOffset,
    topAnnotationLinkDepth,
    bottomAnnotationClientBox,
    bottomAnnotationAnchorOffset,
    bottomAnnotationLinkDepth,
    labelColormap,
    containerWidth,
  },
  containerRef,
) => {
  const classes = useNERLinkStyles();

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  const startAnchorX = topAnnotationClientBox.left + topAnnotationAnchorOffset;
  const startAnchorY = topAnnotationClientBox.top;
  const endAnchorX = bottomAnnotationClientBox.left + bottomAnnotationAnchorOffset;
  const endAnchorY = bottomAnnotationClientBox.top;
  const shoulderX = containerWidth - 15;

  const labelX = (startAnchorX + (isMultiLine ? shoulderX : endAnchorX)) / 2;
  const labelY = startAnchorY - BASE_PADDING - PADDING_PER_DEPTH * topAnnotationLinkDepth;

  const startArrowX = startAnchorX + CURVE_RADIUS + ARROW_PADDING;
  const startArrowY = startAnchorY - BASE_PADDING - PADDING_PER_DEPTH * topAnnotationLinkDepth;
  const endArrowX = isMultiLine
    ? endAnchorX + CURVE_RADIUS + ARROW_PADDING
    : endAnchorX - CURVE_RADIUS - ARROW_PADDING;
  const endArrowY =
    endAnchorY -
    BASE_PADDING -
    PADDING_PER_DEPTH * (bottomAnnotationLinkDepth ?? topAnnotationLinkDepth);

  const pathCommands: string[] = [];
  pathCommands.push(`M ${startAnchorX} ${startAnchorY}`);
  pathCommands.push(
    `v ${CURVE_RADIUS - BASE_PADDING - PADDING_PER_DEPTH * topAnnotationLinkDepth}`,
  );
  pathCommands.push(
    `c 0 ${-BEZIER_CONTROL_DISTANCE}, ${
      CURVE_RADIUS - BEZIER_CONTROL_DISTANCE
    } ${-CURVE_RADIUS}, ${CURVE_RADIUS} ${-CURVE_RADIUS}`,
  );
  if (isMultiLine) {
    pathCommands.push(`H ${shoulderX - CURVE_RADIUS}`);
    pathCommands.push(
      `c ${BEZIER_CONTROL_DISTANCE} 0, ${CURVE_RADIUS} ${
        CURVE_RADIUS - BEZIER_CONTROL_DISTANCE
      }, ${CURVE_RADIUS} ${CURVE_RADIUS}`,
    );
    pathCommands.push(
      `V ${
        endAnchorY - BASE_PADDING - PADDING_PER_DEPTH * bottomAnnotationLinkDepth - CURVE_RADIUS
      }`,
    );
    pathCommands.push(
      `c 0 ${BEZIER_CONTROL_DISTANCE}, ${
        -CURVE_RADIUS + BEZIER_CONTROL_DISTANCE
      } ${CURVE_RADIUS}, ${-CURVE_RADIUS} ${CURVE_RADIUS}`,
    );
    pathCommands.push(`H ${endAnchorX + CURVE_RADIUS}`);
    pathCommands.push(
      `c ${-BEZIER_CONTROL_DISTANCE} 0, ${-CURVE_RADIUS} ${
        CURVE_RADIUS - BEZIER_CONTROL_DISTANCE
      }, ${-CURVE_RADIUS} ${CURVE_RADIUS}`,
    );
  } else {
    pathCommands.push(`H ${endAnchorX - CURVE_RADIUS}`);
    pathCommands.push(
      `c ${BEZIER_CONTROL_DISTANCE} 0, ${CURVE_RADIUS} ${
        CURVE_RADIUS - BEZIER_CONTROL_DISTANCE
      }, ${CURVE_RADIUS} ${CURVE_RADIUS}`,
    );
  }
  pathCommands.push(`V ${endAnchorY}`);

  const renderRightArrow = (x: number, y: number) => {
    const points = [
      `${x - ARROW_WIDTH / 2},${y - ARROW_HEIGHT / 2}`,
      `${x + ARROW_WIDTH / 2},${y}`,
      `${x - ARROW_WIDTH / 2},${y + ARROW_HEIGHT / 2}`,
    ];
    return (
      <polygon points={points.join(' ')} fill={LINK_COLOR} stroke={LINK_COLOR} strokeWidth={2} />
    );
  };

  const renderLeftArrow = (x: number, y: number) => {
    const points = [
      `${x + ARROW_WIDTH / 2},${y - ARROW_HEIGHT / 2}`,
      `${x - ARROW_WIDTH / 2},${y}`,
      `${x + ARROW_WIDTH / 2},${y + ARROW_HEIGHT / 2}`,
    ];
    return (
      <polygon points={points.join(' ')} fill={LINK_COLOR} stroke={LINK_COLOR} strokeWidth={2} />
    );
  };

  const renderArrows = () => {
    const arrows: JSX.Element[] = [];
    if (direction === NERLinkDirection.TopToBottom) {
      arrows.push(renderRightArrow(startArrowX, startArrowY));
      if (isMultiLine) {
        arrows.push(renderLeftArrow(endArrowX, endArrowY));
      } else {
        arrows.push(renderRightArrow(endArrowX, endArrowY));
      }
    } else if (direction === NERLinkDirection.BottomToTop) {
      arrows.push(renderLeftArrow(startArrowX, startArrowY));
      if (isMultiLine) {
        arrows.push(renderRightArrow(endArrowX, endArrowY));
      } else {
        arrows.push(renderLeftArrow(endArrowX, endArrowY));
      }
    }
    return arrows;
  };

  return (
    <>
      <svg viewBox="0 0 1 1" xmlns="http://www.w3.org/2000/svg" className={classes.container}>
        <path
          d={pathCommands.join(' ')}
          stroke={LINK_COLOR}
          strokeDasharray="3"
          fill="transparent"
        />
        {renderArrows()}
      </svg>
      {displayName && (
        <div
          className={classes.label}
          style={{ left: labelX, top: labelY }}
          onClick={() => setIsPopoverOpen(true)}
        >
          {displayName}
        </div>
      )}
      {(isPreview || isPopoverOpen) && (
        <NERLinkPopover
          ref={containerRef}
          anchorX={labelX}
          anchorY={labelY - 20}
          currentRelationshipId={currentRelationshipId}
          validRelationships={validRelationships}
          labelColormap={labelColormap}
          onSetRelationship={onSetRelationship}
          onClose={isPreview ? onPreviewClose! : () => setIsPopoverOpen(false)}
          toggleRelationshipVisibility={toggleRelationshipVisibility}
        />
      )}
    </>
  );
};

const useNERLinkStyles = makeStyles({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 1,
    height: 1,
    overflow: 'visible',
  },

  label: {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    color: rgba('#000', 0.4),
    fontSize: '11px',
    lineHeight: '16px',
    backgroundColor: '#f2f2f2',
    borderRadius: 2,
    paddingLeft: 4,
    paddingRight: 4,
    zIndex: 1,
    cursor: 'pointer',
  },
});

export default React.forwardRef(NERLink);
